// Tracks which city the aircraft is currently flying over via Google reverse
// geocoding (the Maps JS API loads with language:'ja', so results come back in
// Japanese). Requests are throttled hard — minimum ENU movement AND minimum
// interval — so a normal flight stays at a few calls per minute, far inside
// the free tier.
import { loadMapsSdk } from './mapsSdk.js';

const MIN_MOVE_M = 3000;               // don't re-geocode until moved this far
const MIN_INTERVAL_MS = 8000;          // ...and at least this long since the last call
const NO_CITY_MAX_INTERVAL_MS = 32000; // open ocean: stretch the interval while '──' persists
const ERROR_COOLDOWN_MS = 30000;       // quota / network errors: wait before retrying
const NO_CITY = '──';

export async function createCityTracker({ apiKey, onCity }) {
  await loadMapsSdk(apiKey);
  const { Geocoder } = await google.maps.importLibrary('geocoding');
  const geocoder = new Geocoder();

  let disposed = false;
  let inFlight = false;
  let lastName = null;
  let lastX = null; // ENU position (m) of the last successful geocode
  let lastZ = null;
  let nextAllowedAt = 0;
  let intervalMs = MIN_INTERVAL_MS;

  // 市/区 (locality) が最優先。無ければ郡→都道府県/州→国と粗くフォールバック
  // （山間部や砂漠でも何かしら表示される）。
  const TYPE_PRIORITY = [
    'locality',
    'sublocality_level_1',
    'administrative_area_level_2',
    'administrative_area_level_1',
    'country',
  ];
  function extractName(results) {
    for (const type of TYPE_PRIORITY) {
      for (const r of results) {
        const comp = r.address_components?.find((c) => c.types.includes(type));
        if (comp) return comp.long_name;
      }
    }
    return null;
  }

  async function geocode(lat, lng, x, z) {
    try {
      let results = [];
      try {
        ({ results } = await geocoder.geocode({ location: { lat, lng } }));
      } catch (e) {
        // ZERO_RESULTS (open ocean) is a normal outcome — treat as no results.
        if (!String(e?.code ?? e?.message ?? e).includes('ZERO_RESULTS')) throw e;
      }
      lastX = x;
      lastZ = z;
      const name = extractName(results) ?? NO_CITY;
      // 洋上が続く間は間隔を倍々で伸ばし（上限32s）、都市が見つかったら戻す。
      intervalMs = name === NO_CITY
        ? Math.min(NO_CITY_MAX_INTERVAL_MS, intervalMs * 2)
        : MIN_INTERVAL_MS;
      nextAllowedAt = performance.now() + intervalMs;
      if (!disposed && name !== lastName) {
        lastName = name;
        onCity(name);
      }
    } catch (e) {
      nextAllowedAt = performance.now() + ERROR_COOLDOWN_MS;
      console.warn('[cityTracker] geocode failed:', e);
    } finally {
      inFlight = false;
    }
  }

  return {
    // Called at ~15Hz from the frame loop with the aircraft's lat/lng plus its
    // ENU x/z in metres (movement gate — no geographic math needed here).
    // Synchronous so the throttled-out common case allocates nothing.
    maybeUpdate(lat, lng, x, z) {
      if (disposed || inFlight) return;
      if (performance.now() < nextAllowedAt) return;
      if (lastX !== null) {
        const dx = x - lastX;
        const dz = z - lastZ;
        if (dx * dx + dz * dz < MIN_MOVE_M * MIN_MOVE_M) return;
      }
      inFlight = true;
      geocode(lat, lng, x, z);
    },
    dispose() {
      disposed = true;
    },
  };
}
